import { isObject, uniqBy } from 'lodash'
import {
  ref, watch, computed, Ref,
  isRef, ComputedRef, provide, inject,
  onBeforeUnmount,
} from 'vue'
import useStatus from '../status'

export type ItemObjectType = {
  disabled?: boolean
  name: string | number | boolean
  value: string | number | boolean | any
  [prop: string]: any
}

/**
 * option type
 */
export type ItemType = number | boolean | string | ItemObjectType
/**
 * modelValue / value type
 */
export type PossbileValue = ItemType | undefined

type PromiseResult = {
  data: ItemType[]
}

export type ExtendItem = {
  value: string
  text: string
  isCustom: boolean
  isSearch?: boolean
  id: string
  raw: any
}

export type GetListFn = (text: string) => Promise<PromiseResult>

export const defaultFilter = (
  items: ExtendItem[],
  filterText: string,
): ExtendItem[] => {
  if (!filterText) {
    return items
  }
  const re = new RegExp(filterText, 'i')
  return items.filter(item => re.test(item.text))
}

const LOOK_UP_KEYS = [
  'name',
  'value',
  'text',
  'id',
  'key',
]
function tryGetStringFromObj(obj: any): string {
  let str = ''
  if (typeof obj === 'string' || !obj) {
    return str
  }
  LOOK_UP_KEYS.some(i => {
    if (typeof obj[i] === 'string') {
      str = obj[i]
      return true
    }
    return false
  })
  if (!str) {
    return '-'
  }
  return str
}

const defaultItemFactory = (value: PossbileValue): ItemType => ({
  name: typeof value === 'string' ? value : tryGetStringFromObj(value),
  value,
})

const defaultItemToKey = (item: ItemType): string => {
  if (!isObject(item)) {
    return item as string
  }
  if ('value' in item) {
    if (typeof item.value !== 'object') {
      return String(item.value)
    }
    return JSON.stringify(item.value)
  }
  return JSON.stringify(item)
}

const defaultItemToName = (item: ItemType): string => {
  if (!isObject(item)) {
    return item as string
  }
  if ('name' in item) {
    return item.name as string
  }
  return tryGetStringFromObj(item)
}

const defaultItemToValue = (item: ItemType): any => {
  if (!isObject(item)) {
    return item as string
  }
  if ('value' in item) {
    return item.value
  }
  // @ts-ignore
  return item.value || item
}

type ItemUtils = {
  itemToKey?: (item: ItemType) => string
  itemToName?: (item: ItemType) => string
  itemToValue?: (item: ItemType) => any
  itemFactory?: (item: PossbileValue) => ItemType
}
export type UseOptionsParams = {
  options?: Ref<ItemType[]> | ItemType[]
  getList?: GetListFn
  notGetOnEmpty?: boolean
  refreshOnEmpty?: boolean
  filterFn?: (
    options: ExtendItem[],
    keyword: string
  ) => ExtendItem[]
} & {
  [P in keyof ItemUtils]: ItemUtils[P]
}

function getParser(
  params: Pick<
  UseOptionsParams,
  'itemToKey' | 'itemFactory' | 'itemToValue' | 'itemToName'
  >,
) {
  const options = {
    itemToValue: params.itemToValue || defaultItemToValue,
    itemToName: params.itemToName || defaultItemToName,
    itemToKey: params.itemToKey || defaultItemToKey,
    itemFactory: params.itemFactory
      || defaultItemFactory,
  }
  function getComputeItem(i: ItemType, isCustom: boolean) {
    return {
      raw: i,
      isCustom,
      value: options.itemToValue(i),
      text: options.itemToName(i),
      id: options.itemToKey(i),
    }
  }
  return {
    parserOptions: options,
    getComputeItem,
  }
}

export type InputMode = 'select'|'search'|'filter'
type OptionsHook = {
  items: ComputedRef<ExtendItem[]>
  extItems: ComputedRef<ExtendItem[]>
  addOption: ((filterText: PossbileValue) => void)
  removeOption: ((filterText: PossbileValue) => void)
  addSearchOption: (op: ExtendItem) => void
  statusHook: ReturnType<typeof useStatus>
  filterText: Ref<string>
  inputMode: Ref<InputMode>
  getComputeItem: ReturnType<typeof getParser>['getComputeItem']
} & Required<ItemUtils>

/**
 * 获取列表，支持的场景有：
 * 1. options 固定列表
 * 2. getList 通过关键字，调用 API 获取列表
 * 3. filterFn 通过关键字 + 固定的列表，前端筛选列表
 */
export default function useOptions(
  params: UseOptionsParams,
): OptionsHook {
  const filterText = ref('')
  const emptyFilterSearch = ref<ItemType[]>([])
  const searchSource = ref<ItemType[]>([])
  const customSource = ref<ItemType[]>([])
  const searchItems = ref<ExtendItem[]>([])
  const inputMode = ref<InputMode>('select')

  if (isRef(params.options)) {
    watch(params.options, () => {
      customSource.value = []
    })
  }

  const source = computed<ItemType[]>(() => {
    let options: ItemType[] = []
    if (isRef(params.options)) {
      options = params.options.value
    } else {
      options = params.options || []
    }
    if (!params.notGetOnEmpty) {
      options = emptyFilterSearch.value.concat(options)
    }
    return options
  })

  if (params.getList) {
    inputMode.value = 'search'
  } else if (params.filterFn) {
    inputMode.value = 'filter'
  }

  const statusHook = useStatus<ItemType[]|null>(
    () => {
      if (!params.getList) {
        return Promise.resolve([])
      }
      const currentText = filterText.value
      return params.getList(currentText)
        .then(({ data }) => {
          if (currentText === filterText.value) {
            searchSource.value = data
            return data
          }
          return null
        })
    },
  )
  if (params.getList) {
    if (!params.notGetOnEmpty) {
      statusHook.exec()
        .then((ops: ItemType[]|null|void) => {
          if (ops) {
            emptyFilterSearch.value = ops
          }
        })
    }
    watch(filterText, newV => {
      // debounce
      if (params.refreshOnEmpty) {
        statusHook.delayExec()
      } else if (newV) {
        statusHook.delayExec()
      }
    })
  } else {
    watch(source, () => {
      filterText.value = ''
    })
  }
  const {
    parserOptions,
    getComputeItem,
  } = getParser({
    itemToValue: params.itemToValue,
    itemToName: params.itemToName,
    itemToKey: params.itemToKey,
    itemFactory: params.itemFactory,
  })

  const sourceItems = computed(() => {
    let options: any

    if (inputMode.value === 'search') {
      options = searchSource.value.map(i => ({
        ...getComputeItem(i, false),
        isSearch: true,
      }))
    } else {
      options = source.value.map(i => getComputeItem(i, false))
    }
    const items = searchItems.value
      .concat(options)
      .concat(customSource.value
        .map(i => getComputeItem(i, true)))
    return uniqBy(items, i => i.id)
  })

  const showingItems = computed(() => {
    if (filterText.value && inputMode.value !== 'search') {
      return params.filterFn?.(
        sourceItems.value,
        filterText.value,
      ) || []
    }
    return sourceItems.value
  })

  return {
    inputMode,
    filterText,
    items: showingItems,
    extItems: sourceItems,
    addSearchOption: op => {
      if (sourceItems.value.some(i => i.id === op.id)) {
        return
      }
      searchItems.value = searchItems.value.concat(op)
    },
    addOption: text => {
      if (typeof text === 'object') {
        customSource.value = customSource.value
          .concat(text as ItemType)
      } else if (typeof text !== 'undefined') {
        customSource.value = customSource.value
          .concat(parserOptions.itemFactory(text) || [])
      }
    },
    removeOption: text => {
      if (typeof text === 'string') {
        customSource.value = customSource.value.filter(i => {
          if (typeof i === 'object') {
            return i.name !== text
          }
          return i !== text
        })
      } else {
        const lookupName = parserOptions.itemToName(parserOptions.itemFactory(text))
        customSource.value = customSource.value.filter(i => {
          if (typeof i === 'object') {
            return i.name !== lookupName
          }
          return i !== lookupName
        })
      }
    },
    itemToValue: parserOptions.itemToValue,
    itemToKey: parserOptions.itemToKey,
    itemToName: parserOptions.itemToName,
    itemFactory: parserOptions.itemFactory,
    getComputeItem,
    statusHook,
  }
}

interface ValueData {
  selectedItem: Ref<ExtendItem|undefined>
  activeId: Ref<string|undefined>
  setSelectedItem: (o: ExtendItem|null) => any|null
}

export function useSelectValue(
  items: ComputedRef<ExtendItem[]>,
  modelValue: ComputedRef<PossbileValue>,
  optionHook: OptionsHook,
  addIfMissing: boolean,
): ValueData {
  const {
    itemToKey,
    itemFactory,
    addOption,
    addSearchOption,
  } = optionHook
  function findSelectedItem(myValue: PossbileValue, myItems: ExtendItem[]) {
    if (typeof myValue === 'undefined') {
      return undefined
    }
    const expectedId = itemToKey(itemFactory(myValue))
    const value = myItems.find(obj => obj.id === expectedId)
    return value
  }
  const selectedItem = ref<ExtendItem|undefined>(undefined)
  const activeId = computed(() => selectedItem.value?.id)

  watch([modelValue, items], ([newModel, newItems]: any) => {
    const item = findSelectedItem(newModel, newItems)
    if (item
      && item.id === selectedItem.value?.id
      && !selectedItem.value?.isCustom
    ) {
      return
    }
    if (!item && newModel && addIfMissing) {
      addOption(newModel)
    } else {
      selectedItem.value = item
    }
  }, {
    immediate: true,
  })

  return {
    selectedItem,
    activeId,
    setSelectedItem: (op: ExtendItem|null) => {
      if (op?.id === activeId.value) {
        return null
      }
      if (op?.isSearch) {
        addSearchOption(op)
      }
      return op?.value
    },
  }
}

interface ValueListData {
  selectedItems: Ref<ExtendItem[]>
  activeIds: Ref<string[]>
  toggleItem: (o: ExtendItem|null) => any[]|null
}

export function useSelectListValue(
  items: ComputedRef<ExtendItem[]>,
  modelValue: ComputedRef<PossbileValue[]>,
  optionHook: OptionsHook,
  addIfMissing: boolean,
): ValueListData {
  const {
    itemToKey,
    itemFactory,
    addOption,
    addSearchOption,
  } = optionHook
  function findSelectedItem(myValue: PossbileValue, myItems: ExtendItem[]) {
    if (typeof myValue === 'undefined') {
      return undefined
    }
    const expectedId = itemToKey(itemFactory(myValue))
    return myItems.find(obj => obj.id === expectedId)
  }
  const selectedItems = ref<ExtendItem[]>([])
  const activeIds = computed(() => selectedItems.value.map(i => i.id))

  watch([modelValue, items], ([newModel, newItems]) => {
    const itemList: ExtendItem[] = []

    newModel.forEach((i: any) => {
      const item = findSelectedItem(i, newItems)
      if (!item && i && addIfMissing) {
        addOption(i)
      } else if (item) {
        itemList.push(item)
      }
    })
    selectedItems.value = itemList
  }, {
    immediate: true,
  })

  return {
    selectedItems,
    activeIds,
    toggleItem: (op: ExtendItem|null) => {
      if (op && activeIds.value.indexOf(op?.id) > -1) {
        const values = selectedItems.value.filter(i => i.id !== op.id)
        return values.map(i => i.value)
      }
      if (op?.isSearch) {
        addSearchOption(op)
      }
      if (!op) {
        return selectedItems.value.map(i => i.value)
      }
      const val = selectedItems.value.concat(op)
      return val.map(i => i.value)
    },
  }
}

type OptionContext = {
  addOption: OptionsHook['addOption']
  removeOption: OptionsHook['removeOption']
  fallback: boolean
  [x: string]: any
}

export function provideOptionsContext(
  name: string,
  optionHook: OptionsHook,
  extra?: Record<string, any>,
): void {
  provide(name, {
    fallback: false,
    valueToItem(v: PossbileValue) {
      if (typeof v !== 'undefined') {
        return optionHook.getComputeItem(optionHook.itemFactory(v), false)
      }
      return undefined
    },
    addOption: optionHook.addOption,
    removeOption: optionHook.removeOption,
    ...extra,
  })
}

export function injectOptionsContext(
  name: string,
  val: PossbileValue,
  modelValue: unknown,
): [OptionContext, boolean] {
  const ctx = inject<OptionContext>(name, {
    addOption: () => undefined,
    removeOption: () => undefined,
    fallback: true,
  })
  const isFallback = ctx.fallback || typeof modelValue !== 'undefined'
  if (!isFallback) {
    ctx.addOption(val)
    onBeforeUnmount(() => {
      ctx.removeOption(val)
    })
  }
  return [ctx, isFallback]
}
